import React from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import { CameraIcon, CheckIcon, LockOpenIcon } from 'lucide-react';
import { BrandMark } from '../components/BrandMark';
import { HabitItem } from '../components/HabitItem';
import { MoodSelector } from '../components/MoodSelector';
import { PageHeader } from '../components/PageHeader';
import { XPIndicator } from '../components/XPIndicator';
import { Button } from '../components/ui/Button';
import { Card } from '../components/ui/Card';
import { Modal } from '../components/ui/Modal';
import { useLocale } from '../contexts/LocaleContext';
import { useProgress } from '../contexts/ProgressContext';
import { HydrationMeter } from '../components/HydrationMeter';
import { useHydration } from '../contexts/HydrationContext';
import { formatLiters } from '../utils/hydration';
import { feedbackKey } from '../utils/progress';
export function DailyCheck() {
  const {
    t,
    locale
  } = useLocale();
  const progress = useProgress();
  const hydration = useHydration();
  const feedback = feedbackKey(progress.dailyXp, progress.completedCount);
  const celebration = progress.celebration;
  return <div className="px-2 pb-2" data-id="element-2519" data-mp="1ss1mo5zvijkh">
      <PageHeader title={t('check.title')} subtitle={t('check.subtitle')} data-id="element-2520" data-mp="1srrss7bpjs9i" />

      <Card tone="dark" className="rounded-panel p-3" data-id="element-2521" data-mp="1os989cnstj77">
        <div className="flex items-end justify-between gap-2" data-id="element-2522" data-mp="1jtpmpdd4a9fd">
          <div data-id="element-2523" data-mp="1jr3fjeu51vxo">
            <p className="bee-overline text-[9px] font-bold text-ondark" data-id="element-2524" data-mp="2gix7uk9tqgir">
              {t('check.earned')}
            </p>
            <p className="mt-1 flex items-baseline gap-1" data-id="element-2525" data-mp="2cin47e9i8272">
              <span className="bee-num text-[44px] font-bold leading-none text-sand" data-id="element-2526" data-mp="27iildhl4ivdo">
                {progress.dailyXp}
              </span>
              <span className="bee-num text-[13px] font-medium text-ondark" data-id="element-2527" data-mp="22icqztngyurw">
                /{progress.baseTotalXp}
              </span>
            </p>
          </div>
          <div className="text-right" data-id="element-2528" data-mp="r4ioyglhlnv6">
            <p className="bee-overline text-[9px] font-bold text-ondark" data-id="element-2529" data-mp="r3head8dv3dz">
              {t('check.completed')}
            </p>
            <p className="bee-num mt-1 text-[15px] font-bold text-sand" data-id="element-2530" data-mp="sjbm1ifhha0s">
              {progress.completedCount} {t('common.of')} {progress.habits.length}
            </p>
            <p className="bee-num mt-[2px] text-[11px] text-ondark" data-id="element-2531" data-mp="jjycyqv9ungl">
              {t('check.remaining')}: {progress.remainingXp}
            </p>
          </div>
        </div>

        <div className="mt-3" data-id="element-2532" data-mp="9uojb6ks4wqd">
          <XPIndicator xp={progress.dailyXp} tone="dark" data-id="element-2533" data-mp="9tdh3b6zfx1d" />
        </div>

        <AnimatePresence initial={false} mode="wait" data-id="element-2534" data-mp="elwr5y6goa1f">
          {feedback && <motion.p key={feedback} initial={{
          opacity: 0,
          y: 4
        }} animate={{
          opacity: 1,
          y: 0
        }} exit={{
          opacity: 0
        }} transition={{
          duration: 0.2,
          ease: [0.23, 1, 0.32, 1]
        }} className="mt-1 text-[13px] font-medium text-sand" data-id="element-2535" data-mp="us19nswaw4a">
              {t(feedback)}
            </motion.p>}
        </AnimatePresence>

        {progress.postUnlocked && <p className="mt-2 flex items-center gap-1 text-[11px] font-medium text-gold" data-id="element-2536" data-mp="38ezx0l16igk8">
            <LockOpenIcon size={13} aria-hidden="true" data-id="element-2537" data-mp="33h7xb4ualq5h" />
            {t('check.unlocked')}
          </p>}
      </Card>

      <ul className="mt-2 space-y-1" data-id="element-2538" data-mp="3iakuq6z7suo6">
        {progress.habits.filter(habit => habit.id !== 'mood').map(habit => habit.id === 'hydration' ? <li key={habit.id} className="rounded-card border border-sand-line bg-white/40 p-1" data-id="element-2539" data-mp="3rbj0atpaxa8v">
                <HabitItem habit={habit} completed={progress.completed.hydration} goal={hydration.completion === 'manual' ? t('hydration.manualDone') : hydration.targetMl ? t('hydration.goal', {
          target: formatLiters(hydration.targetMl, locale)
        }) : t('hydration.goalNoWeight')} data-id="element-2540" data-mp="3mcg6k5eruqg7" />
                <div className="px-1 pb-1 pt-2" data-id="element-2541" data-mp="28eo0lbz793wp">
                  <HydrationMeter variant="full" data-id="element-2542" data-mp="28d38dt0cxx20" />
                  {hydration.completion !== 'intake' && <button type="button" onClick={hydration.completion === 'manual' ? hydration.undoManual : hydration.markManual} className="mt-1 flex min-h-[44px] items-center text-[12px] font-medium text-soft underline-offset-2 transition-colors duration-150 ease-brand hover:text-deep hover:underline" data-id="element-2543" data-mp="2d7qytxex91j8">
                      {hydration.completion === 'manual' ? t('hydration.manualUndo') : t('hydration.manualMark')}
                    </button>}
                </div>
              </li> : <li key={habit.id} data-id="element-2544" data-mp="y7idiyqq999u">
                <HabitItem habit={habit} completed={progress.completed[habit.id]} onToggle={() => progress.toggleHabit(habit.id)} data-id="element-2545" data-mp="yao1ubrj874i" />
              </li>)}
      </ul>

      <Card className="mt-2 p-2" data-id="element-2546" data-mp="khydw1sg0s4r">
        <div className="flex items-baseline justify-between gap-2" data-id="element-2547" data-mp="kgwr5022eh77">
          <h2 className="text-section text-deep" data-id="element-2548" data-mp="fj50e9d5p14l">{t('check.mood.title')}</h2>
          <span className={`bee-num text-[11px] font-bold ${progress.completed.mood ? 'text-gold-deep' : 'text-muted'}`} data-id="element-2549" data-mp="aj8yqgrxlls7">
            {progress.xpFor('mood')} {t('common.xp')}
          </span>
        </div>
        <div className="mt-2" data-id="element-2550" data-mp="1k5ayzhtki7d">
          <MoodSelector value={progress.mood} onChange={progress.setMood} data-id="element-2551" data-mp="20vew9x5jvvei" />
        </div>

        <AnimatePresence initial={false} data-id="element-2552" data-mp="1vwbzezzq4vhm">
          {progress.mood === 'drained' && <motion.div initial={{
          opacity: 0,
          height: 0
        }} animate={{
          opacity: 1,
          height: 'auto'
        }} exit={{
          opacity: 0,
          height: 0
        }} transition={{
          duration: 0.24,
          ease: [0.23, 1, 0.32, 1]
        }} className="overflow-hidden" data-id="element-2553" data-mp="1vuhcxh8t0vbf">
              <div className="mt-2 rounded-card border border-gold/40 bg-sand-deep/60 p-2" data-id="element-2554" data-mp="25yfn6ndqelun">
                <p className="text-[13px] font-medium text-deep" data-id="element-2555" data-mp="2awz9wq05g1o2">
                  {t('home.gentle.body')}
                </p>
                <ul className="mt-1 space-y-1" data-id="element-2556" data-mp="32q9js50g9slj">
                  {['1', '2', '3', '4'].map(key => <li key={key} className="flex items-center gap-2 text-[12.5px] text-muted" data-id="element-2557" data-mp="2or9sepljyzlo">
                      <span className="h-[5px] w-[5px] shrink-0 rounded-pill bg-gold" aria-hidden="true" data-id="element-2558" data-mp="2trxpnt0dkcoc" />
                      {t(`home.gentle.${key}`)}
                    </li>)}
                </ul>
              </div>
            </motion.div>}
        </AnimatePresence>
      </Card>

      {progress.dayComplete && <Card className="mt-2 border-gold/45 p-2" data-id="element-2559" data-mp="1jruzwllkmr2b">
          <div className="flex items-start gap-2" data-id="element-2560" data-mp="1jqjw4ls4ljb7">
            <span className="grid h-10 w-10 shrink-0 place-items-center rounded-[13px] bg-deep text-gold" data-id="element-2561" data-mp="1et0a2zp6s28p">
              <CameraIcon size={19} data-id="element-2562" data-mp="r2o4hvheyhla" />
            </span>
            <div className="min-w-0 flex-1" data-id="element-2563" data-mp="r3fsak4rrb10">
              <h2 className="text-section text-deep" data-id="element-2564" data-mp="n247crwubdz5">{t('check.bonus.title')}</h2>
              <p className="mt-[2px] text-[12px] leading-snug text-muted" data-id="element-2565" data-mp="10szbxs0b38wx">
                {t('check.bonus.body')}
              </p>
            </div>
          </div>
          {progress.photoBonus ? <p className="mt-2 flex items-center gap-1 text-[12px] font-medium text-gold-deep" data-id="element-2566" data-mp="8cesxuzqldgx">
              <CheckIcon size={14} strokeWidth={3} aria-hidden="true" data-id="element-2567" data-mp="3dlh0703luiv" />
              {t('check.bonus.done')}
            </p> : <Button variant="outline" fullWidth className="mt-2" onClick={progress.claimPhotoBonus} data-id="element-2568" data-mp="3eyh6s0ufy3mi">
              {t('check.bonus.cta')} · +{progress.photoBonusXp} {t('common.xp')}
            </Button>}
        </Card>}

      <Modal open={celebration?.kind === 'dayComplete'} onClose={progress.dismissCelebration} labelledBy="day-complete-title" data-id="element-2569" data-mp="3sxqai11gn59l">
        <div className="px-1 pb-1 pt-3 text-center" data-id="element-2570" data-mp="5rzdztjcsaef">
          <BrandMark size={48} className="mx-auto" data-id="element-2571" data-mp="5qeio1p7snfi" />
          <h2 id="day-complete-title" className="mt-2 text-title text-deep" data-id="element-2572" data-mp="aifs7fv0001u">
            {t('check.complete.title')}
          </h2>
          <p className="mx-auto mt-1 max-w-[28ch] text-[13px] leading-relaxed text-muted" data-id="element-2573" data-mp="fjva4z1vpqg0">
            {t('check.complete.body', {
            n: progress.streak
          })}
          </p>
          <Button variant="primary" size="lg" fullWidth className="mt-3" onClick={progress.dismissCelebration} data-id="element-2574" data-mp="pgq7vfqbvmt8">
            {t('common.continue')}
          </Button>
        </div>
      </Modal>

      <Modal open={celebration?.kind === 'badge'} onClose={progress.dismissCelebration} labelledBy="badge-title" data-id="element-2575" data-mp="1zzjqf5bfhp5t">
        <div className="px-1 pb-1 pt-3 text-center" data-id="element-2576" data-mp="1v2t6g6wi69f4">
          <span className="mx-auto grid h-16 w-16 place-items-center rounded-pill border border-gold/50 bg-deep" data-id="element-2577" data-mp="2sp0c7k4jm7t2">
            <BrandMark size={30} data-id="element-2578" data-mp="2nqp35bm2o1bw" />
          </span>
          <p className="bee-overline mt-2 text-[9px] font-bold text-gold-deep" data-id="element-2579" data-mp="2ns08vgz2vj5n">
            {t('check.badge.title')}
          </p>
          <h2 id="badge-title" className="mt-1 text-title text-deep" data-id="element-2580" data-mp="2e0mc3gyigxg7">
            {celebration?.kind === 'badge' ? t(celebration.badgeId) : ''}
          </h2>
          <p className="mt-1 text-[13px] text-muted" data-id="element-2581" data-mp="3bh2x57o7wa94">
            {celebration?.kind === 'badge' ? t('check.badge.body', {
            days: celebration.days
          }) : ''}
          </p>
          <Button variant="gold" size="lg" fullWidth className="mt-3" onClick={progress.dismissCelebration} data-id="element-2582" data-mp="w3xu6yuxwtjf">
            {t('common.continue')}
          </Button>
        </div>
      </Modal>
    </div>;
}